<template>
    <div class="tool-block my-2" :class="blockClass">
        <!-- Header row -->
        <div class="flex items-center gap-2 px-3 py-1.5">
            <UIcon :name="statusIcon" class="w-4 h-4 flex-shrink-0" :class="iconClass" />
            <span class="font-medium text-slate-700">Upload Image</span>
            <span class="flex-1 min-w-0" />
            <span v-if="isToolProcessing" class="text-[11px] text-blue-500 animate-pulse whitespace-nowrap">preparing…</span>
            <span v-else-if="uploadState === 'uploading'" class="text-[11px] text-blue-500 animate-pulse whitespace-nowrap">uploading…</span>
            <span v-else-if="uploadState === 'done'" class="text-[11px] text-green-600 whitespace-nowrap">uploaded</span>
        </div>

        <!-- Upload card (tool output available, not yet uploaded) -->
        <div v-if="showUploadCard" class="mx-3 mb-2">
            <div class="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4">
                <div class="flex items-center gap-3">
                    <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                        <UIcon name="i-heroicons-photo" class="w-5 h-5 text-blue-600" />
                    </div>
                    <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-slate-700">Upload Image</p>
                        <p class="text-xs text-slate-500 mt-0.5">PNG, JPEG, GIF, or WebP — max 10MB</p>
                    </div>
                    <UButton color="primary" size="sm" @click="openFilePicker">
                        <UIcon name="i-heroicons-arrow-up-tray" class="w-4 h-4 mr-1" />
                        Choose Image
                    </UButton>
                </div>
            </div>
            <input ref="fileInput" type="file" accept="image/png,image/jpeg,image/gif,image/webp" class="hidden" @change="handleFileSelect" />
        </div>

        <!-- Success with image preview -->
        <div v-if="uploadState === 'done' && uploadResult" class="mx-3 mb-2">
            <div class="rounded-lg border border-green-200 bg-green-50 p-3">
                <div class="flex items-center gap-2">
                    <UIcon name="i-heroicons-check-circle" class="w-5 h-5 text-green-600 flex-shrink-0" />
                    <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-green-800">Image uploaded</p>
                        <p class="text-xs text-green-600 mt-0.5 truncate">{{ uploadResult.fileName }}</p>
                    </div>
                </div>
                <img :src="uploadResult.imageUrl" class="mt-2 max-h-40 rounded border border-green-200" />
            </div>
        </div>

        <!-- Error state -->
        <div v-if="uploadState === 'error'" class="mx-3 mb-2 px-2.5 py-2 rounded-md bg-red-50 border border-red-100 text-red-600 text-xs">
            {{ uploadError }}
        </div>
    </div>
</template>

<script setup lang="ts">
const props = defineProps<{
    part: {
        type: string;
        toolCallId?: string;
        state?: string;
        input?: unknown;
        output?: unknown;
        errorText?: string;
        [key: string]: unknown;
    };
}>();

const emit = defineEmits<{
    'upload-complete': [result: { imageUrl: string; fileName: string }];
}>();

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

const fileInput = ref<HTMLInputElement | null>(null);
const uploadState = ref<'idle' | 'uploading' | 'done' | 'error'>('idle');
const uploadResult = ref<{ imageUrl: string; fileName: string } | null>(null);
const uploadError = ref('');

// Tool state detection
const isToolProcessing = computed(() => {
    if (props.part.output !== undefined) return false;
    if (props.part.errorText) return false;
    return true;
});

const isToolCompleted = computed(() =>
    props.part.state === 'output-available' ||
    (props.part.output !== undefined && !props.part.errorText)
);

const showUploadCard = computed(() =>
    isToolCompleted.value && uploadState.value === 'idle'
);

// Display
const statusIcon = computed(() => {
    if (props.part.errorText) return 'i-heroicons-x-circle';
    if (uploadState.value === 'done') return 'i-heroicons-check-circle';
    if (uploadState.value === 'error') return 'i-heroicons-x-circle';
    if (isToolProcessing.value || uploadState.value === 'uploading') return 'i-heroicons-arrow-path';
    return 'i-heroicons-photo';
});

const iconClass = computed(() => {
    if (props.part.errorText || uploadState.value === 'error') return 'text-red-500';
    if (uploadState.value === 'done') return 'text-green-500';
    if (isToolProcessing.value || uploadState.value === 'uploading') return 'text-blue-500 animate-spin';
    return 'text-blue-500';
});

const blockClass = computed(() => {
    if (props.part.errorText || uploadState.value === 'error') return 'tool-block--error';
    if (uploadState.value === 'done') return 'tool-block--success';
    if (isToolProcessing.value || uploadState.value === 'uploading') return 'tool-block--active';
    return 'tool-block--active';
});

function openFilePicker() {
    fileInput.value?.click();
}

async function handleFileSelect(event: Event) {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
        uploadState.value = 'error';
        uploadError.value = `Unsupported type: ${file.type}. Use PNG, JPEG, GIF, or WebP.`;
        return;
    }
    if (file.size > MAX_FILE_SIZE) {
        uploadState.value = 'error';
        uploadError.value = 'Image exceeds maximum size of 10MB.';
        return;
    }

    uploadState.value = 'uploading';
    try {
        const formData = new FormData();
        formData.append('image', file);
        const result = await $fetch<{ url: string }>('/api/media/upload-image', {
            method: 'POST',
            body: formData,
        });
        uploadState.value = 'done';
        uploadResult.value = { imageUrl: result.url, fileName: file.name };
        emit('upload-complete', { imageUrl: result.url, fileName: file.name });
    } catch (err) {
        uploadState.value = 'error';
        uploadError.value = err instanceof Error ? err.message : 'Upload failed';
    }
}
</script>

<style scoped>
.tool-block {
    border-left: 3px solid rgb(203 213 225);
    border-radius: 6px;
    background: rgb(248 250 252 / 0.6);
    font-size: 12px;
}

.tool-block--error {
    border-left-color: rgb(239 68 68);
    background: rgb(254 242 242 / 0.5);
}

.tool-block--active {
    border-left-color: rgb(59 130 246);
    background: rgb(239 246 255 / 0.5);
}

.tool-block--success {
    border-left-color: rgb(34 197 94);
    background: rgb(240 253 244 / 0.4);
}
</style>
